import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { Task, TaskPriority, TaskStatus } from '@callhub/shared';
import { Button, Input } from '@/components/ui-next';
import { api } from '@/lib/api';
import { buildDialerPath } from '@/lib/routes';

function formatDateLabel(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString();
}

function priorityBadge(priority: TaskPriority): string {
  if (priority === 'high') return 'bg-red-500/15 text-red-700 dark:text-red-300';
  if (priority === 'low') return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300';
  return 'bg-amber-500/15 text-amber-700 dark:text-amber-300';
}

export function CrmTasksPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [myWorkOnly, setMyWorkOnly] = useState(true);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskPriority, setNewTaskPriority] = useState<TaskPriority>('medium');
  const [newTaskDueAt, setNewTaskDueAt] = useState('');

  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      const response = await api.getMe();
      return response.data;
    },
  });

  const statusesQuery = useQuery({
    queryKey: ['crm-task-statuses'],
    queryFn: async () => {
      const response = await api.getTaskStatuses();
      return response.data;
    },
    retry: false,
  });

  const tasksQuery = useQuery({
    queryKey: ['crm-tasks-board', myWorkOnly, meQuery.data?.id],
    queryFn: async () => {
      const response = await api.getTasks({
        assignedUserId: myWorkOnly ? meQuery.data?.id : undefined,
        includeCompleted: true,
      });
      return response.data;
    },
    enabled: statusesQuery.isSuccess && (!myWorkOnly || Boolean(meQuery.data?.id)),
    retry: false,
  });

  const statuses = useMemo(() => {
    return [...(statusesQuery.data ?? [])].sort((a, b) => a.position - b.position);
  }, [statusesQuery.data]);

  const tasksByStatus = useMemo(() => {
    const grouped = new Map<string, Task[]>();
    for (const status of statuses) {
      grouped.set(status.id, []);
    }
    for (const task of tasksQuery.data ?? []) {
      if (!grouped.has(task.statusId)) grouped.set(task.statusId, []);
      grouped.get(task.statusId)?.push(task);
    }
    for (const [statusId, tasks] of grouped) {
      grouped.set(
        statusId,
        [...tasks].sort((left, right) => left.position - right.position),
      );
    }
    return grouped;
  }, [statuses, tasksQuery.data]);

  const defaultStatus = useMemo(() => {
    return (
      statuses.find((status) => status.key === 'todo') ??
      statuses.find((status) => !status.isTerminal) ??
      statuses[0] ??
      null
    );
  }, [statuses]);

  const createTaskMutation = useMutation({
    mutationFn: async () => {
      const title = newTaskTitle.trim();
      if (!title) throw new Error('Task title is required');
      if (!defaultStatus) throw new Error('No task statuses configured');
      return api.createTask({
        title,
        priority: newTaskPriority,
        dueAt: newTaskDueAt ? new Date(newTaskDueAt).toISOString() : null,
        statusId: defaultStatus.id,
      });
    },
    onSuccess: async () => {
      setNewTaskTitle('');
      setNewTaskPriority('medium');
      setNewTaskDueAt('');
      await queryClient.invalidateQueries({ queryKey: ['crm-tasks-board'] });
    },
  });

  const moveTaskMutation = useMutation({
    mutationFn: async (payload: { taskId: string; statusId: string }) => {
      return api.moveTaskStatus(payload.taskId, { statusId: payload.statusId });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['crm-tasks-board'] });
    },
  });

  const completionMutation = useMutation({
    mutationFn: async (payload: { taskId: string; status: TaskStatus }) => {
      return api.moveTaskStatus(payload.taskId, { statusId: payload.status.id });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['crm-tasks-board'] });
    },
  });

  if (statusesQuery.isError || tasksQuery.isError) {
    const error =
      (statusesQuery.error as Error | undefined)?.message ??
      (tasksQuery.error as Error | undefined)?.message ??
      'CRM task board unavailable.';

    return (
      <section className="rounded-xl border border-border bg-card p-6">
        <h2 className="text-xl font-semibold text-foreground">My Work</h2>
        <p className="mt-2 text-sm text-muted-foreground">{error}</p>
        <p className="mt-2 text-xs text-muted-foreground">
          Enable backend flag <code>CRM_V2_ENABLED=true</code> and refresh.
        </p>
      </section>
    );
  }

  return (
    <div className="space-y-4">
      <header className="rounded-xl border border-border bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-2xl font-semibold text-foreground">My Work</h2>
            <p className="text-sm text-muted-foreground">
              Scrum board for all contact tasks with one-click execution.
            </p>
          </div>
          <label className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground">
            <input
              type="checkbox"
              className="h-4 w-4 accent-brand-500"
              checked={myWorkOnly}
              onChange={(event) => setMyWorkOnly(event.target.checked)}
            />
            My tasks only
          </label>
        </div>

        <div className="mt-4 grid gap-2 md:grid-cols-4">
          <Input
            value={newTaskTitle}
            onChange={(event) => setNewTaskTitle(event.target.value)}
            placeholder="Add a new task"
            className="bg-background md:col-span-2"
          />
          <select
            value={newTaskPriority}
            onChange={(event) => setNewTaskPriority(event.target.value as TaskPriority)}
            className="h-10 rounded-xl border border-border bg-background px-3 text-sm text-foreground"
          >
            <option value="low">Low priority</option>
            <option value="medium">Medium priority</option>
            <option value="high">High priority</option>
          </select>
          <Input
            type="datetime-local"
            value={newTaskDueAt}
            onChange={(event) => setNewTaskDueAt(event.target.value)}
            className="bg-background"
          />
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => createTaskMutation.mutate()}
            isDisabled={createTaskMutation.isPending || !newTaskTitle.trim()}
          >
            {createTaskMutation.isPending ? 'Adding...' : 'Create Task'}
          </Button>
          {createTaskMutation.isError ? (
            <p className="text-xs text-red-500">{(createTaskMutation.error as Error).message}</p>
          ) : null}
        </div>
      </header>

      {tasksQuery.isLoading || statusesQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading tasks...</p>
      ) : (
        <div className="grid gap-3 overflow-x-auto md:grid-cols-2 xl:grid-cols-4">
          {statuses.map((status) => {
            const tasks = tasksByStatus.get(status.id) ?? [];
            return (
              <section
                key={status.id}
                className="min-h-[340px] rounded-xl border border-border bg-card p-3"
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  const taskId = event.dataTransfer.getData('text/task-id');
                  if (!taskId) return;
                  moveTaskMutation.mutate({ taskId, statusId: status.id });
                }}
              >
                <div className="mb-2 flex items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-foreground">{status.label}</h3>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                    {tasks.length}
                  </span>
                </div>

                <div className="space-y-2">
                  {tasks.map((task) => {
                    const doneStatus =
                      statuses.find((statusRow) => statusRow.key === 'done') ?? null;
                    return (
                      <article
                        key={task.id}
                        draggable
                        onDragStart={(event) => event.dataTransfer.setData('text/task-id', task.id)}
                        className="rounded-lg border border-border bg-background/70 p-3"
                      >
                        <p className="text-sm font-medium text-foreground">{task.title}</p>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                          <span
                            className={`rounded-full px-2 py-0.5 ${priorityBadge(task.priority)}`}
                          >
                            {task.priority}
                          </span>
                          <span>Due: {formatDateLabel(task.dueAt)}</span>
                        </div>

                        {task.contactName || task.contactPhone ? (
                          <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-border bg-background px-2 py-1 text-xs">
                            <button
                              type="button"
                              className="font-medium text-foreground hover:underline"
                              onClick={() => {
                                if (!task.contactId) return;
                                navigate(`/contacts?contact=${encodeURIComponent(task.contactId)}`);
                              }}
                            >
                              {task.contactName ?? task.contactPhone}
                            </button>
                            {task.contactPhone ? (
                              <button
                                type="button"
                                className="rounded border border-border px-2 py-0.5 text-muted-foreground"
                                onClick={() => {
                                  navigate(
                                    buildDialerPath({
                                      contact: task.contactId,
                                      phone: task.contactPhone,
                                      action: 'call',
                                    }),
                                  );
                                }}
                              >
                                Call now
                              </button>
                            ) : null}
                          </div>
                        ) : null}

                        <div className="mt-2 flex items-center gap-2">
                          {doneStatus && status.id !== doneStatus.id ? (
                            <button
                              type="button"
                              className="rounded border border-border px-2 py-1 text-xs text-foreground"
                              onClick={() =>
                                completionMutation.mutate({ taskId: task.id, status: doneStatus })
                              }
                            >
                              Mark done
                            </button>
                          ) : null}
                        </div>
                      </article>
                    );
                  })}

                  {tasks.length === 0 ? (
                    <p className="rounded border border-dashed border-border px-2 py-6 text-center text-xs text-muted-foreground">
                      Drop tasks here
                    </p>
                  ) : null}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {moveTaskMutation.isError ? (
        <p className="text-sm text-red-500">{(moveTaskMutation.error as Error).message}</p>
      ) : null}
    </div>
  );
}
